import {
  firstOrUndefined,
  isDisposalType,
  SiteEntity,
  startOfDay,
  statisticsType,
  subDays,
  useGetSiteStatistics
} from "@ots-frontend/ots-data";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
  ConnectedSiteRecentTickets,
  type ContainerTicketParty,
  formatMapNumber,
  readContainerStatusDisplay,
  SiteInfoPanelView,
  type SiteNearbyUnitsProps,
  summarizeSiteContainers,
  useLiveMapNow
} from "@ots-frontend/ots-ui-tw";
import {useGetContainerStatuses} from "../../../../../hooks/useGetContainerStatuses";
import {useApp} from "../../../../../providers/RegionProvider/RegionProvider";
import {useOpsStatuses} from "../../../../../hooks/useOpsStatuses";
import {useQuickPanelClose} from "../../QuickPanelContainer/QuickPanelContainer";
import {renderMapEntityAnchor} from "../map-entity-anchor";
import {toOpsStatusView} from "../OpsStatusInfoPanel/OpsStatusInfoPanel";

export type SiteInfoPanelProps = {
  site: SiteEntity
  /** The units the map has drawn lines to from this site. */
  nearby?: SiteNearbyUnitsProps
  /** Whether ticket rows name the hauler (HubGo) or the driver (DispatchGo). */
  ticketParty?: ContainerTicketParty
  /** Opens a ticket's profile; the app owns the route. */
  onOpenTicket?: (tta: {id: string; database?: string}) => void
}

/** The live map's site panel: fetches the site's containers, ops status and recent volume for the TW view. */
export const SiteInfoPanel = (props: SiteInfoPanelProps) => {
  const {site, nearby, ticketParty, onOpenTicket} = props

  const {t} = useTranslation(["translation", "common"])
  const {appCategory, regionId} = useApp()
  const navigate = useNavigate()
  const onClose = useQuickPanelClose()
  const now = useLiveMapNow()

  const {containerStatuses} = useGetContainerStatuses({
    filter: {
      site: site,
    }
  })
  // Stale polls follow the browser's Container Status display options, so both screens agree.
  const containers = useMemo(() => summarizeSiteContainers(containerStatuses, {
    now,
    category: appCategory,
    display: readContainerStatusDisplay(),
  }), [containerStatuses, now, appCategory])

  const {opsLocationStatuses} = useOpsStatuses({
    regionId: regionId,
    siteIds: [site.id]
  })
  const opsStatus = firstOrUndefined(opsLocationStatuses)
  const ops = useMemo(
    () => opsStatus ? toOpsStatusView(opsStatus, t as unknown as Parameters<typeof toOpsStatusView>[1]) : undefined,
    [opsStatus, t]
  )

  const since = useMemo(() => startOfDay(subDays(new Date(), 1)).getTime(), [])
  const {statistics, isLoading} = useGetSiteStatistics({
    site: site,
    pickup: !isDisposalType(site.type),
    fromDttm: since,
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
    includeTruckingData: true,
    includePipelineData: true,
  })

  return (
    <SiteInfoPanelView
      site={site}
      containers={containers}
      now={now}
      ops={ops}
      nearby={nearby}
      recentTickets={
        <ConnectedSiteRecentTickets
          key={site.id}
          siteId={site.id}
          party={ticketParty}
          renderEntity={renderMapEntityAnchor}
          onOpenTicket={onOpenTicket}/>
      }
      insights={{
        since,
        loading: isLoading,
        quantity: formatMapNumber(statistics?.getValue(undefined, statisticsType.quantity)),
        loads: formatMapNumber(statistics?.getValue(undefined, statisticsType.loads), 0),
      }}
      renderEntity={renderMapEntityAnchor}
      onOpenProfile={() => navigate(`/sites/profile?id=${site.id}`)}
      onClose={onClose}/>
  )
}

export const SiteInfoPanelMemo = React.memo(
  SiteInfoPanel,
  (prevProps, nextProps) => prevProps.site.id === nextProps.site.id && prevProps.nearby === nextProps.nearby
)
